import './IconPicker.css';
import { useState } from 'react';
import { IconButton, Typography, Box, Popover, Tooltip } from '@mui/material';
import { FaLink } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import DynamicIcon from '../DynamicIcon/DynamicIcon';
import { socialMediaOptions, SocialMediaOption } from './Options';

const sortedOptions = [...socialMediaOptions].sort((a, b) => (a.label < b.label ? -1 : 1));

interface IconPickerProps {
    value: SocialMediaOption | null;
    onChange: (option: SocialMediaOption) => void;
}

const IconPicker = ({ value, onChange }: IconPickerProps) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleSelect = (option: SocialMediaOption) => {
        onChange(option);
        handleClose();
    };

    return (
        <>
            <Tooltip title={value?.label ?? t('profile.links.link.select-media')} placement="top">
                <IconButton onClick={handleOpen} size="small" className="icon-picker-trigger">
                    {value
                        ? <DynamicIcon name={value.icon} idIcon={value.id} isGlobal={false} fontSize={18} />
                        : <FaLink fontSize={16} />
                    }
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: { border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 0.5 }
                    }
                }}
            >
                <Box className="icon-picker-popover">
                    <Typography variant="caption" className="icon-picker-label">
                        {t('profile.links.link.select-media')}
                    </Typography>
                    <Box className="icon-picker-grid">
                        {sortedOptions.map(option => (
                            <Tooltip title={option.label} placement="top" key={option.id}>
                                <IconButton
                                    size="small"
                                    onClick={() => handleSelect(option)}
                                    className={`icon-picker-option${value?.id === option.id ? ' icon-picker-option--selected' : ''}`}
                                >
                                    <DynamicIcon name={option.icon} idIcon={option.id} isGlobal={false} fontSize={20} />
                                </IconButton>
                            </Tooltip>
                        ))}
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default IconPicker;
